import type { DataProvider } from "ra-core";

import type { ConfigurationContextValue } from "../root/ConfigurationContext";
import type { Deal } from "../types";

export type DealsByStage = Record<Deal["stage"], Deal[]>;

export const getDealsByStage = (
  unorderedDeals: Deal[],
  dealStages: ConfigurationContextValue["dealStages"],
) => {
  if (!dealStages) return {};
  const dealsByStage: Record<Deal["stage"], Deal[]> = unorderedDeals.reduce(
    (acc, deal) => {
      // if deal has a stage that does not exist in configuration, assign it to the first stage
      const stage = dealStages.find((s) => s.value === deal.stage)
        ? deal.stage
        : dealStages[0].value;
      acc[stage].push(deal);
      return acc;
    },
    dealStages.reduce(
      (obj, stage) => ({ ...obj, [stage.value]: [] }),
      {} as Record<Deal["stage"], Deal[]>,
    ),
  );
  // order each column by index
  dealStages.forEach((stage) => {
    dealsByStage[stage.value] = dealsByStage[stage.value].sort(
      (recordA: Deal, recordB: Deal) => recordA.index - recordB.index,
    );
  });
  return dealsByStage;
};

export const updateDealStageLocal = (
  sourceDeal: Deal,
  source: { stage: string; index: number },
  destination: { stage: string; index?: number },
  dealsByStage: DealsByStage,
): DealsByStage => {
  if (source.stage === destination.stage) {
    const column = [...dealsByStage[source.stage]];
    column.splice(source.index, 1);
    column.splice(destination.index ?? column.length + 1, 0, sourceDeal);
    return { ...dealsByStage, [destination.stage]: column };
  } else {
    const sourceColumn = [...dealsByStage[source.stage]];
    const destinationColumn = [...(dealsByStage[destination.stage] ?? [])];
    sourceColumn.splice(source.index, 1);
    destinationColumn.splice(
      destination.index ?? destinationColumn.length + 1,
      0,
      sourceDeal,
    );
    return {
      ...dealsByStage,
      [source.stage]: sourceColumn,
      [destination.stage]: destinationColumn,
    };
  }
};

export const updateDealStageRemote = async (
  source: Deal,
  destination: { stage: string; index?: number },
  dataProvider: DataProvider,
) => {
  if (source.stage === destination.stage) {
    const { data: columnDeals } = await dataProvider.getList("deals", {
      sort: { field: "index", order: "ASC" },
      pagination: { page: 1, perPage: 100 },
      filter: { stage: source.stage },
    });
    const destinationIndex = destination.index ?? columnDeals.length + 1;
    if (source.index > destinationIndex) {
      await Promise.all([
        ...columnDeals
          .filter((d) => d.index >= destinationIndex && d.index < source.index)
          .map((d) =>
            dataProvider.update("deals", {
              id: d.id,
              data: { index: d.index + 1 },
              previousData: d,
            }),
          ),
        dataProvider.update("deals", {
          id: source.id,
          data: { index: destinationIndex },
          previousData: source,
        }),
      ]);
    } else {
      await Promise.all([
        ...columnDeals
          .filter((d) => d.index <= destinationIndex && d.index > source.index)
          .map((d) =>
            dataProvider.update("deals", {
              id: d.id,
              data: { index: d.index - 1 },
              previousData: d,
            }),
          ),
        dataProvider.update("deals", {
          id: source.id,
          data: { index: destinationIndex },
          previousData: source,
        }),
      ]);
    }
  } else {
    const [{ data: sourceDeals }, { data: destinationDeals }] =
      await Promise.all([
        dataProvider.getList("deals", {
          sort: { field: "index", order: "ASC" },
          pagination: { page: 1, perPage: 100 },
          filter: { stage: source.stage },
        }),
        dataProvider.getList("deals", {
          sort: { field: "index", order: "ASC" },
          pagination: { page: 1, perPage: 100 },
          filter: { stage: destination.stage },
        }),
      ]);
    const destinationIndex = destination.index ?? destinationDeals.length + 1;
    await Promise.all([
      ...sourceDeals
        .filter((d) => d.index > source.index)
        .map((d) =>
          dataProvider.update("deals", {
            id: d.id,
            data: { index: d.index - 1 },
            previousData: d,
          }),
        ),
      ...destinationDeals
        .filter((d) => d.index >= destinationIndex)
        .map((d) =>
          dataProvider.update("deals", {
            id: d.id,
            data: { index: d.index + 1 },
            previousData: d,
          }),
        ),
      dataProvider.update("deals", {
        id: source.id,
        data: { index: destinationIndex, stage: destination.stage },
        previousData: source,
      }),
    ]);
  }
};
